/**
 * Sales Teams Configuration Tab
 *
 * Builds sales teams at each rooftop: a team is a Sales Manager plus the
 * salespeople assigned to them — the manager's name is the team name.
 * Assignments drive the Sales Team Report (Reports → New Vehicles).
 * Editable by Admin/CEO/Executive, GeneralManagers, and NEW-dept
 * DepartmentManagers (any rooftop) — see canEditSalesTeamsConfig.
 *
 * UX (manager-first):
 *  - Pick a location, then a Sales Manager (the team) from a dropdown of
 *    managers active at that location this month.
 *  - Check off salespeople to add them to the selected manager's team.
 *    Checking fires a PUT immediately (also reassigns from another team);
 *    unchecking fires a DELETE. Inline "Saved ✓" / "Failed" indicator.
 *  - A roster panel shows every team at the location, plus Unassigned.
 *
 * List sources: sales-manager-performance and salesperson-performance for the
 * selected location over the current + prior two months (records are monthly,
 * so this approximates "deals in the past 60 days" — keeps the lists populated
 * at the start of a month before anyone has sold). The saved
 * `salespersonId`/`managerId` are DealerBuilt employee ids — stable across
 * months. `dmsSalesRepId` is stored when known: it is the report's primary
 * join key to funnel rows (name matching is the fallback).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getSalesManagerPerformance,
  getSalespersonPerformance,
  saveSalesTeamAssignment,
  deleteSalesTeamAssignment,
  SalesTeamAssignmentConfig,
  LocationScheduleConfig,
} from '../../services/api';

// Vehicle-selling rooftops (matches the Sales Team Report — Loc 12 & 44 are
// Body Shop and excluded).
const VEHICLE_LOCATION_IDS = [1, 2, 3, 4, 5, 10, 11, 14];

interface ManagerOption {
  managerId: string;
  managerName: string;
}

interface SalespersonRow {
  salespersonId: string;
  salespersonName: string;
  dmsSalesRepId?: string;
}

type RowStatus = 'idle' | 'saving' | 'saved' | 'error';

interface SalesTeamsTabProps {
  canEdit: boolean;
  locationSchedules: LocationScheduleConfig[];
  salesTeamAssignments: SalesTeamAssignmentConfig[];
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  refresh: () => Promise<void>;
}

export const SalesTeamsTab: React.FC<SalesTeamsTabProps> = ({
  canEdit,
  locationSchedules,
  salesTeamAssignments,
  onSuccess,
  onError,
  refresh,
}) => {
  const [selectedLocationId, setSelectedLocationId] = useState<number>(VEHICLE_LOCATION_IDS[0]);
  const [selectedManagerId, setSelectedManagerId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [managers, setManagers] = useState<ManagerOption[]>([]);
  const [salespeople, setSalespeople] = useState<SalespersonRow[]>([]);
  const [rowStatus, setRowStatus] = useState<Record<string, RowStatus>>({});

  const now = useMemo(() => new Date(), []);
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  const visibleLocations = useMemo(() => {
    return VEHICLE_LOCATION_IDS
      .map((id) => locationSchedules.find((l) => l.locationId === id))
      .filter((l): l is LocationScheduleConfig => Boolean(l));
  }, [locationSchedules]);

  // Guards against a slow response for a previously selected location landing
  // after the user switched: only the latest request may commit state, so the
  // checklist can never pair another location's people with selectedLocationId.
  const fetchGeneration = useRef(0);

  // Ref keeps the (inline-arrow) onError prop out of fetchLocationPeople's deps
  // so location changes — not parent re-renders — drive the refetch effect.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Fetch manager + salesperson lists for the selected location over the
  // current + prior two months (monthly records; ~past 60 days), merged and
  // deduped with the most recent month's name winning.
  const fetchLocationPeople = useCallback(async (locationId: number) => {
    const generation = ++fetchGeneration.current;
    setLoading(true);

    // Newest first, so first-write-wins merging keeps the latest names/ids.
    const months: { year: number; month: number }[] = [];
    for (let i = 0; i <= 2; i++) {
      const d = new Date(year, month - 1 - i, 1);
      months.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
    }

    const monthResults = await Promise.all(
      months.map(async (m) => {
        const [managerResponse, salespersonResponse] = await Promise.all([
          getSalesManagerPerformance(locationId, m.year, m.month),
          getSalespersonPerformance(locationId, m.year, m.month),
        ]);
        return { managerResponse, salespersonResponse };
      }),
    );
    if (generation !== fetchGeneration.current) return; // stale — a newer location was selected

    const anySuccess = monthResults.some(
      (r) => r.managerResponse.success || r.salespersonResponse.success,
    );
    if (!anySuccess) {
      setManagers([]);
      setSalespeople([]);
      setSelectedManagerId('');
      setLoading(false);
      onErrorRef.current(
        monthResults[0].managerResponse.error ||
        monthResults[0].salespersonResponse.error ||
        'Failed to load sales staff',
      );
      return;
    }

    const managerMap = new Map<string, ManagerOption>();
    const salespersonMap = new Map<string, SalespersonRow>();
    for (const { managerResponse, salespersonResponse } of monthResults) {
      if (managerResponse.success && managerResponse.data) {
        for (const m of managerResponse.data.managers as any[]) {
          if (!m.managerId || !m.managerName) continue;
          const id = String(m.managerId);
          if (!managerMap.has(id)) {
            managerMap.set(id, { managerId: id, managerName: m.managerName as string });
          }
        }
      }
      if (salespersonResponse.success && salespersonResponse.data) {
        for (const sp of salespersonResponse.data.salespeople as any[]) {
          if (!sp.salespersonId || !sp.salespersonName) continue;
          const id = String(sp.salespersonId);
          const existing = salespersonMap.get(id);
          if (!existing) {
            salespersonMap.set(id, {
              salespersonId: id,
              salespersonName: sp.salespersonName as string,
              dmsSalesRepId: sp.dmsSalesRepId ? String(sp.dmsSalesRepId) : undefined,
            });
          } else if (!existing.dmsSalesRepId && sp.dmsSalesRepId) {
            // Backfill the join key from an older month when the newer record lacks it.
            existing.dmsSalesRepId = String(sp.dmsSalesRepId);
          }
        }
      }
    }

    const managerRows = Array.from(managerMap.values())
      .sort((a, b) => a.managerName.localeCompare(b.managerName));
    const salespersonRows = Array.from(salespersonMap.values())
      .sort((a, b) => a.salespersonName.localeCompare(b.salespersonName));

    setManagers(managerRows);
    setSalespeople(salespersonRows);
    setSelectedManagerId((prev) =>
      managerRows.some((m) => m.managerId === prev) ? prev : (managerRows[0]?.managerId ?? ''));
    setLoading(false);
  }, [year, month]);

  useEffect(() => {
    fetchLocationPeople(selectedLocationId);
  }, [fetchLocationPeople, selectedLocationId]);

  // Index this location's assignments by salespersonId for O(1) lookups.
  const assignmentBySalesperson = useMemo(() => {
    const map = new Map<string, SalesTeamAssignmentConfig>();
    for (const a of salesTeamAssignments) {
      if (a.locationId === selectedLocationId) map.set(a.salespersonId, a);
    }
    return map;
  }, [salesTeamAssignments, selectedLocationId]);

  // Roster: assignments grouped by team, plus active salespeople with none.
  const teamRoster = useMemo(() => {
    const byTeam = new Map<string, { managerName: string; members: string[] }>();
    for (const a of Array.from(assignmentBySalesperson.values())) {
      const team = byTeam.get(a.managerId) ?? { managerName: a.managerName, members: [] };
      team.members.push(a.salespersonName);
      byTeam.set(a.managerId, team);
    }
    const teams = Array.from(byTeam.values())
      .map((t) => ({ ...t, members: t.members.sort((a, b) => a.localeCompare(b)) }))
      .sort((a, b) => a.managerName.localeCompare(b.managerName));
    const unassigned = salespeople
      .filter((sp) => !assignmentBySalesperson.has(sp.salespersonId))
      .map((sp) => sp.salespersonName);
    return { teams, unassigned };
  }, [assignmentBySalesperson, salespeople]);

  const selectedManager = managers.find((m) => m.managerId === selectedManagerId);

  const flashStatus = (key: string, status: RowStatus) => {
    setRowStatus((prev) => ({ ...prev, [key]: status }));
    if (status === 'saved' || status === 'error') {
      setTimeout(() => {
        setRowStatus((prev) => {
          if (prev[key] !== status) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }, 2500);
    }
  };

  const handleToggle = async (salesperson: SalespersonRow, checked: boolean) => {
    if (!canEdit || !selectedManager) return;
    const key = `${selectedLocationId}_${salesperson.salespersonId}`;
    flashStatus(key, 'saving');

    if (checked) {
      const response = await saveSalesTeamAssignment({
        type: 'SALES_TEAM_ASSIGNMENT',
        locationId: selectedLocationId,
        salespersonId: salesperson.salespersonId,
        salespersonName: salesperson.salespersonName,
        dmsSalesRepId: salesperson.dmsSalesRepId,
        managerId: selectedManager.managerId,
        managerName: selectedManager.managerName,
      });
      if (response.success) {
        await refresh();
        flashStatus(key, 'saved');
        onSuccess(`${salesperson.salespersonName} → Team ${selectedManager.managerName}`);
      } else {
        flashStatus(key, 'error');
        onError(response.error || 'Failed to save assignment');
      }
      return;
    }

    const response = await deleteSalesTeamAssignment(selectedLocationId, salesperson.salespersonId);
    if (response.success) {
      await refresh();
      flashStatus(key, 'saved');
      onSuccess(`${salesperson.salespersonName} removed from Team ${selectedManager.managerName}`);
    } else {
      flashStatus(key, 'error');
      onError(response.error || 'Failed to clear assignment');
    }
  };

  return (
    <div>
      <p className="text-sm text-gray-600 mb-4">
        Build sales teams for the Sales Team Report (Reports → New Vehicles): pick a location and a
        {' '}<strong>Sales Manager</strong> — the manager's name is the team name — then check off the
        salespeople on that team. Salespeople without a team fall into the report's "Unassigned"
        section. Lists show managers and salespeople with deals in roughly the past 60 days
        (current and prior two months).
      </p>

      <div className="flex flex-wrap gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
          <select
            value={selectedLocationId}
            onChange={(e) => setSelectedLocationId(parseInt(e.target.value, 10))}
            className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          >
            {visibleLocations.map((l) => (
              <option key={l.locationId} value={l.locationId}>{l.locationName}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sales Manager (team)</label>
          <select
            value={selectedManagerId}
            onChange={(e) => setSelectedManagerId(e.target.value)}
            disabled={loading || managers.length === 0}
            className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm disabled:opacity-60"
          >
            {managers.length === 0 && <option value="">No managers this month</option>}
            {managers.map((m) => (
              <option key={m.managerId} value={m.managerId}>{m.managerName}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading sales staff...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Assignment checklist for the selected manager */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-3">
              {selectedManager
                ? <>Assign salespeople to <span className="text-blue-700">Team {selectedManager.managerName}</span></>
                : 'No sales managers with recent activity at this location'}
            </h3>
            {salespeople.length === 0 ? (
              <div className="text-sm text-gray-500 italic py-4 text-center">
                No salespeople with deals at this location in the past ~60 days.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {salespeople.map((sp) => {
                  const key = `${selectedLocationId}_${sp.salespersonId}`;
                  const assignment = assignmentBySalesperson.get(sp.salespersonId);
                  const onSelectedTeam = assignment?.managerId === selectedManagerId;
                  const status = rowStatus[key];
                  return (
                    <li key={sp.salespersonId} className="flex items-center justify-between py-2">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={onSelectedTeam}
                          onChange={(e) => handleToggle(sp, e.target.checked)}
                          disabled={!canEdit || !selectedManager || status === 'saving'}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60"
                        />
                        <span className="text-sm font-medium text-gray-900">{sp.salespersonName}</span>
                        <span className="text-xs text-gray-400 font-mono">#{sp.salespersonId}</span>
                        {!sp.dmsSalesRepId && (
                          <span
                            className="text-xs text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-1.5 py-0.5 rounded"
                            title="No DMS sales rep id on file — the report will match this person by name only"
                          >
                            name-match only
                          </span>
                        )}
                      </label>
                      <span className="text-xs ml-3">
                        {status === 'saving' && <span className="text-gray-500">Saving…</span>}
                        {status === 'saved' && <span className="text-green-700 font-medium">Saved ✓</span>}
                        {status === 'error' && <span className="text-rose-700 font-medium">Failed</span>}
                        {!status && assignment && !onSelectedTeam && (
                          <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            Team {assignment.managerName}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            {canEdit && selectedManager && salespeople.length > 0 && (
              <p className="text-xs text-gray-500 mt-3">
                Checking a salesperson already on another team moves them to Team {selectedManager.managerName}.
              </p>
            )}
          </div>

          {/* Current team roster at this location */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="font-medium text-gray-900 mb-3">Current teams at this location</h3>
            {teamRoster.teams.length === 0 ? (
              <div className="text-sm text-gray-500 italic">No teams yet — assign a salesperson to create one.</div>
            ) : (
              <div className="space-y-3">
                {teamRoster.teams.map((team) => (
                  <div key={team.managerName}>
                    <div className="text-sm font-semibold text-gray-800">
                      Team {team.managerName}
                      <span className="ml-2 text-xs font-normal text-gray-500">({team.members.length})</span>
                    </div>
                    <div className="text-sm text-gray-600 ml-3">{team.members.join(', ')}</div>
                  </div>
                ))}
              </div>
            )}
            {teamRoster.unassigned.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="text-sm font-semibold text-gray-800">
                  Unassigned
                  <span className="ml-2 text-xs font-normal text-gray-500">({teamRoster.unassigned.length})</span>
                </div>
                <div className="text-sm text-gray-600 ml-3">{teamRoster.unassigned.join(', ')}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
